/**
 * SharePoint opslag-analyse — vind waar de opslag zit en ruim op.
 * Sites op grootte → door de mappen (op grootte) → grote/media-bestanden →
 * PowerPoint-versie-opblaas → oude versies opschonen.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { bevestig } from '../../../utils/bevestig';
import {
  Box, Typography, Stack, Paper, Table, TableHead, TableBody, TableRow, TableCell,
  Button, Chip, CircularProgress, Alert, Link, Breadcrumbs, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import HistoryIcon from '@mui/icons-material/History';
import RefreshIcon from '@mui/icons-material/Refresh';
import { BACKEND_URL } from '../../../utils/apiConfig';
import { authFetch } from '../../../utils/authHelpers';
import { useUserRole } from '../../../contexts/UserRoleContext';

const api = (path, opts) => authFetch(`${BACKEND_URL}/Api/sharepoint${path}`, opts).then(async (r) => {
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.error || d.detail || `Fout ${r.status}`);
  return d;
});

// Map-URL afleiden uit de bestand-URL (bestandsnaam eraf) → opent de MAP in SharePoint
// i.p.v. het bestand te downloaden, zodat je 'm daar kunt verwijderen.
const folderUrl = (fileUrl) => {
  if (!fileUrl) return null;
  try {
    const u = new URL(fileUrl);
    const parts = u.pathname.split('/'); parts.pop();
    u.pathname = parts.join('/'); u.search = ''; u.hash = '';
    return u.toString();
  } catch { return null; }
};

// Opslag in drie getallen: wat er nu staat, wat oude versies (en prullenbak) innemen, en samen.
const VERSIE_KLEUR = '#c2410c';
const gbTekst = (v) => `${Number(v || 0).toLocaleString('nl-NL', { maximumFractionDigits: 1 })} GB`;

function Opslagsplitsing({ titel, huidig, versies, totaal, versieLabel = 'oude versies', toelichting }) {
  const pct = totaal > 0 ? Math.min(100, Math.round((versies / totaal) * 100)) : 0;
  return (
    <Paper variant="outlined" sx={{ mb: 1.5, px: 2, py: 1.25 }}>
      <Stack direction="row" spacing={3} alignItems="baseline" flexWrap="wrap" useFlexGap>
        <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ minWidth: 120 }}>{titel}</Typography>
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Huidige bestanden</Typography>
          <Typography variant="body2" fontWeight={700} sx={{ fontVariantNumeric: 'tabular-nums' }}>{gbTekst(huidig)}</Typography>
        </Box>
        <Box>
          <Typography variant="caption" sx={{ display: 'block', color: VERSIE_KLEUR }}>{versieLabel[0].toUpperCase() + versieLabel.slice(1)}</Typography>
          <Typography variant="body2" fontWeight={700} sx={{ color: VERSIE_KLEUR, fontVariantNumeric: 'tabular-nums' }}>{gbTekst(versies)}{totaal > 0 ? ` · ${pct}%` : ''}</Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Totaal</Typography>
          <Typography variant="body2" fontWeight={700} sx={{ fontVariantNumeric: 'tabular-nums' }}>{gbTekst(totaal)}</Typography>
        </Box>
      </Stack>
      {totaal > 0 && (
        <Box sx={{ mt: 1, height: 6, borderRadius: 3, bgcolor: '#e2e8f0', overflow: 'hidden', display: 'flex' }} aria-hidden>
          <Box sx={{ width: `${100 - pct}%`, bgcolor: '#64748b' }} />
          <Box sx={{ width: `${pct}%`, bgcolor: VERSIE_KLEUR }} />
        </Box>
      )}
      {toelichting && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>{toelichting}</Typography>}
    </Paper>
  );
}

// Groottecel van een map/bestand: totaal incl. versies, met de splitsing eronder; eerlijk als iets niet gemeten is.
// Kleine aantallen niet als "0 GB" tonen: dat leest als "niets".
const kleinGb = (gb, bytes) => (bytes > 0 && !(gb >= 0.01) ? '<0,01' : gb);

function GrootteCel({ f, vet = true, toonOnbekend = false }) {
  const gemeten = f.versions_bytes !== undefined && f.versions_bytes !== null;
  const oud = kleinGb(f.versions_gb, f.versions_bytes);
  if (f.size_unknown) {
    return <TableCell align="right"><Typography variant="caption" color="text.secondary">grootte niet te lezen</Typography></TableCell>;
  }
  return (
    <TableCell align="right" sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
      {vet ? <b>{f.size_gb} GB</b> : <>{f.size_gb} GB</>}
      {gemeten && f.versions_bytes > 0 && (
        <Tooltip title={`${f.stream_gb} GB huidige bestanden + ${oud} GB oude versies${f.version_count ? ` (${f.version_count} versies)` : ''}`}>
          <Box component="span" sx={{ display: 'block', fontSize: 11, color: VERSIE_KLEUR }}>{f.stream_gb} + {oud} GB versies</Box>
        </Tooltip>
      )}
      {gemeten && !(f.versions_bytes > 0) && (
        <Box component="span" sx={{ display: 'block', fontSize: 11, color: '#94a3b8' }}>geen oude versies</Box>
      )}
      {!gemeten && toonOnbekend && (
        <Box component="span" sx={{ display: 'block', fontSize: 11, color: '#94a3b8' }}>versies niet gemeten</Box>
      )}
    </TableCell>
  );
}

export default function SharePointAnalysis() {
  const { permissions } = useUserRole();
  // Opschonen (versies verwijderen) is destructief → alleen met edit-recht.
  const canEdit = !!permissions?.sharepoint_analyse?.edit;
  const [configured, setConfigured] = useState(null);
  const [sites, setSites] = useState(null);
  const [totalGb, setTotalGb] = useState(0);
  const [sitesSplit, setSitesSplit] = useState(null);   // { current_gb, versions_gb, unmeasured_sites }
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Browse-state
  const [site, setSite] = useState(null);              // { site_url, name }
  const [driveId, setDriveId] = useState(null);
  const [librariesLevel, setLibrariesLevel] = useState(false); // op de bibliotheek-keuze (>1 drive): scannen hier zou alleen de standaardbib. pakken
  const [trail, setTrail] = useState([]);              // [{id, name}] breadcrumb (id null = root)
  const [browsing, setBrowsing] = useState(false);
  const [content, setContent] = useState(null);        // { folders, files }

  const [verDlg, setVerDlg] = useState(null);          // { file, loading, data }
  const [toast, setToast] = useState(null);

  const [sitesAt, setSitesAt] = useState(null); // wanneer de sites-lijst is opgehaald

  useEffect(() => {
    api('/configured').then((d) => setConfigured(d.configured)).catch(() => setConfigured(false));
    // Sites-lijst onthouden: toon de laatst opgehaalde lijst meteen (Graph-enumeratie is
    // traag) — je hoeft dus niet elke keer opnieuw op 'Analyseer sites' te klikken.
    try {
      const cached = JSON.parse(localStorage.getItem('sp-sites-cache') || 'null');
      if (cached?.sites?.length) { setSites(cached.sites); setTotalGb(cached.total_gb || 0); setSitesAt(cached.at || null); setSitesSplit(cached.split || null); }
    } catch { /* geen cache */ }
  }, []);

  const loadSites = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const d = await api('/sites');
      setSites(d.sites || []); setTotalGb(d.total_gb || 0);
      const split = d.versions_gb !== undefined
        ? { current_gb: d.current_gb, versions_gb: d.versions_gb, unmeasured_sites: d.unmeasured_sites || 0, unmeasured_gb: d.unmeasured_gb || 0,
          quota_unknown_sites: d.quota_unknown_sites || 0, complete: d.complete !== false }
        : null;
      setSitesSplit(split);
      const at = new Date().toISOString(); setSitesAt(at);
      try { localStorage.setItem('sp-sites-cache', JSON.stringify({ sites: d.sites || [], total_gb: d.total_gb || 0, split, at })); } catch { /* quota */ }
    }
    catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, []);

  const [scan, setScan] = useState(null);              // { loading, files, truncated }
  const [deep, setDeep] = useState(null);              // { loading | running | ...result }

  // Achtergrond-scan: pollt /deep-scan/saved tot 'done'. pollRef zodat we 'm kunnen
  // stoppen bij navigeren/unmount; pollSavedRef om vanuit openFolder (stabiele
  // callback) de laatste versie aan te roepen zonder stale closure.
  const pollRef = useRef(null);
  const clearPoll = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  const pollSaved = (s, itemId, driveId) => {
    clearPoll();
    const q = `site_id=${encodeURIComponent(s.site_id)}${itemId ? `&item_id=${encodeURIComponent(itemId)}` : ''}${driveId ? `&drive_id=${encodeURIComponent(driveId)}` : ''}`;
    let ticks = 0;
    pollRef.current = setInterval(async () => {
      // Plafond (±16 min = backend running→error-grens) zodat een tab niet eeuwig blijft pollen.
      if (++ticks > 240) { clearPoll(); setError('Diepe analyse duurt te lang / niet gevonden — herlaad en probeer opnieuw.'); setDeep(null); return; }
      try {
        const sv = await api(`/deep-scan/saved?${q}`);
        if (!sv?.found) return;
        if (sv.status === 'done') { clearPoll(); setDeep({ loading: false, fromCache: false, ...sv }); }
        else if (sv.status === 'error') { clearPoll(); setError('Diepe analyse mislukt — probeer een niveau dieper te scannen.'); setDeep(null); }
        else { setDeep({ running: true, scanned_folders: sv.scanned_folders || 0 }); }
      } catch { /* blijf pollen */ }
    }, 4000);
  };
  const pollSavedRef = useRef(pollSaved);
  pollSavedRef.current = pollSaved;
  useEffect(() => clearPoll, []); // cleanup bij unmount

  const openFolder = useCallback(async (s, item, newTrail) => {
    clearPoll();
    setBrowsing(true); setError(null); setContent(null); setScan(null); setDeep(null);
    try {
      const idParam = s.site_id ? `site_id=${encodeURIComponent(s.site_id)}` : `site_url=${encodeURIComponent(s.site_url)}`;
      const driveParam = item?.drive_id ? `&drive_id=${encodeURIComponent(item.drive_id)}` : '';
      const d = await api(`/browse?${idParam}${driveParam}${item?.id ? `&item_id=${encodeURIComponent(item.id)}` : ''}`);
      setDriveId(d.drive_id);
      setLibrariesLevel(!!d.libraries); // >1 bibliotheek → scannen pas na een bibliotheek te openen
      setContent({ folders: d.folders || [], files: d.files || [], totals: d.totals || null, site_totals: d.site_totals || null, metrics_source: d.metrics_source });
      setTrail(newTrail);
      // De drive waarop deze scope draait (root van een 1-bib-site heeft geen item.drive_id).
      const dv = item?.drive_id || d.drive_id || null;
      // Bewaarde/lopende diepe analyse voor deze scope automatisch tonen.
      if (s.site_id && !d.libraries) {
        api(`/deep-scan/saved?site_id=${encodeURIComponent(s.site_id)}${item?.id ? `&item_id=${encodeURIComponent(item.id)}` : ''}${dv ? `&drive_id=${encodeURIComponent(dv)}` : ''}`)
          .then((sv) => {
            if (!sv?.found) return;
            if (sv.status === 'running') { setDeep({ running: true, scanned_folders: sv.scanned_folders || 0 }); pollSavedRef.current(s, item?.id || null, dv); }
            else setDeep({ loading: false, fromCache: true, ...sv });
          })
          .catch(() => {});
      }
    } catch (e) { setError(e.message); }
    finally { setBrowsing(false); }
  }, []);

  const runScan = async () => {
    const cur = trail[trail.length - 1];
    setScan({ loading: true }); setError(null);
    try {
      const idParam = site.site_id ? `site_id=${encodeURIComponent(site.site_id)}` : `site_url=${encodeURIComponent(site.site_url)}`;
      const dv = cur?.drive_id || driveId;  // root van een 1-bib-site: val terug op de resolved drive
      const driveParam = dv ? `&drive_id=${encodeURIComponent(dv)}` : '';
      const d = await api(`/scan-large-files?${idParam}${driveParam}${cur?.id ? `&item_id=${encodeURIComponent(cur.id)}` : ''}&min_mb=50`);
      setDriveId(d.drive_id);
      setScan({ loading: false, files: d.files || [], truncated: d.truncated });
    } catch (e) { setError(e.message); setScan(null); }
  };

  const runDeepScan = async () => {
    const cur = trail[trail.length - 1];
    setDeep({ running: true, scanned_folders: 0 }); setScan(null); setError(null);
    try {
      const idParam = site.site_id ? `site_id=${encodeURIComponent(site.site_id)}` : `site_url=${encodeURIComponent(site.site_url)}`;
      const meta = `&site_name=${encodeURIComponent(site.name || '')}${cur?.id ? `&scope_name=${encodeURIComponent(cur.name || '')}` : ''}`;
      const dv = cur?.drive_id || driveId;  // root van een 1-bib-site: val terug op de resolved drive
      const driveParam = dv ? `&drive_id=${encodeURIComponent(dv)}` : '';
      // Achtergrond-scan: mag zo lang duren als nodig (grote/hoge mappen). We pollen op dezelfde scope.
      await api(`/deep-scan?${idParam}${driveParam}${cur?.id ? `&item_id=${encodeURIComponent(cur.id)}` : ''}${meta}&background=true`);
      pollSaved(site, cur?.id || null, dv);
    } catch (e) { setError(e.message); setDeep(null); }
  };

  const enterSite = (s) => { setSite(s); openFolder(s, null, [{ id: null, name: s.name || 'Root' }]); };
  const enterFolder = (f) => openFolder(site, f, [...trail, { id: f.id, name: f.name, web_url: f.web_url, drive_id: f.drive_id }]);
  const gotoCrumb = (i) => { const t = trail.slice(0, i + 1); openFolder(site, t[t.length - 1], t); };
  const backToSites = () => { clearPoll(); setSite(null); setContent(null); setTrail([]); setDeep(null); };

  const openVersions = async (file) => {
    setVerDlg({ file, loading: true, data: null });
    try {
      const d = await api(`/versions?drive_id=${encodeURIComponent(driveId)}&item_id=${encodeURIComponent(file.id)}`);
      setVerDlg({ file, loading: false, data: d });
    } catch (e) { setVerDlg({ file, loading: false, data: { error: e.message } }); }
  };

  const trimVersions = async (file) => {
    if (!(await bevestig({ titel: `Oude versies van "${file.name}" verwijderen?`, tekst: 'Alleen de huidige versie blijft staan. Dit kan niet ongedaan worden gemaakt.', knop: 'Verwijderen', gevaarlijk: true }))) return;
    setVerDlg((d) => ({ ...d, loading: true }));
    try {
      const d = await api('/trim-versions', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ drive_id: driveId, item_id: file.id, keep: 1 }),
      });
      setToast(`${d.deleted} versies verwijderd — ${d.freed_gb} GB vrijgemaakt${d.errors ? ` (${d.errors} mislukt)` : ''}.`);
      setVerDlg(null);
    } catch (e) { setToast(`Opschonen mislukt: ${e.message}`); setVerDlg((d) => (d ? { ...d, loading: false } : d)); }
  };

  if (configured === false) {
    return (
      <Alert severity="warning" sx={{ m: 1 }}>
        De SharePoint-analyse is nog niet geconfigureerd. Er is een Azure AD app-registratie nodig met
        <b> Sites.ReadWrite.All</b> + <b>Reports.Read.All</b> (admin-consent), en de client-id/secret als
        <code> MS_GRAPH_SHAREPOINT_CLIENT_ID/SECRET</code> in de AI Engine. Zie <code>docs/SHAREPOINT_STORAGE.md</code>.
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 1 }}>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6" fontWeight={700} sx={{ flex: 1 }}>🗄️ SharePoint opslag-analyse</Typography>
        {sites && <Chip label={`Totaal ${totalGb} GB`} color="warning" sx={{ fontWeight: 700 }} />}
        {sites && sitesAt && (
          <Typography variant="caption" color="text.disabled" sx={{ whiteSpace: 'nowrap' }}>
            bijgewerkt {new Date(sitesAt).toLocaleString('nl-NL', { dateStyle: 'short', timeStyle: 'short' })}
          </Typography>
        )}
        <Button variant="outlined" size="small" startIcon={<RefreshIcon />} onClick={loadSites} disabled={loading}>
          {sites ? 'Ververs' : 'Analyseer sites'}
        </Button>
      </Stack>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {loading && <CircularProgress size={22} />}

      {/* Sites op grootte */}
      {!site && sites && sitesSplit && (
        <Opslagsplitsing titel="Alle sites" huidig={sitesSplit.current_gb} versies={sitesSplit.versions_gb}
          totaal={Math.round(((sitesSplit.current_gb || 0) + (sitesSplit.versions_gb || 0)) * 100) / 100}
          versieLabel="oude versies + prullenbak"
          toelichting={`Totaal = wat SharePoint rekent voor de opslaglimiet. Het verschil met de huidige bestanden zijn vooral oude versies, plus prullenbak (93 dagen) en de bewaarbibliotheek.${sitesSplit.unmeasured_sites ? ` ${sitesSplit.unmeasured_sites} site(s) (${sitesSplit.unmeasured_gb} GB) konden niet gesplitst worden en staan hier buiten; samen met die sites is het ${totalGb} GB.` : ''}${sitesSplit.quota_unknown_sites ? ` Van ${sitesSplit.quota_unknown_sites} site(s) was de opslag niet te lezen; die ontbreken ook in het totaal.` : ''}${sitesSplit.complete === false ? ' De meting liep tegen de tijdslimiet aan: ververs voor de rest.' : ''}`} />
      )}
      {!site && sites && (
        <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
          <Table size="small">
            <TableHead><TableRow sx={{ bgcolor: '#f8fafc' }}>
              <TableCell><b>Site</b></TableCell><TableCell align="right"><b>Totaal</b></TableCell>
              <TableCell align="right"><b>Huidige bestanden</b></TableCell>
              <TableCell align="right"><b style={{ color: VERSIE_KLEUR }}>Oude versies + prullenbak</b></TableCell>
            </TableRow></TableHead>
            <TableBody>
              {sites.map((s) => (
                <TableRow key={s.site_url || s.name} hover>
                  <TableCell>
                    {s.site_url ? <Link component="button" onClick={() => enterSite(s)} sx={{ fontWeight: 600, textAlign: 'left' }}>{s.name}</Link>
                      : <Typography variant="body2">{s.name}</Typography>}
                  </TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                    {s.quota_known === false || s.storage_used_gb == null
                      ? <Typography variant="caption" color="text.secondary">niet te lezen</Typography>
                      : <b>{s.storage_used_gb} GB</b>}
                  </TableCell>
                  {s.is_subsite ? (
                    <TableCell align="right" colSpan={2}><Typography variant="caption" color="text.secondary">subsite — telt mee bij de hoofdsite</Typography></TableCell>
                  ) : (
                    <>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{s.current_gb != null ? `${s.current_gb} GB` : '-'}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', color: VERSIE_KLEUR }}>{s.versions_gb != null ? `${s.versions_gb} GB` : '-'}</TableCell>
                    </>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      {/* Browse binnen een site */}
      {site && (
        <>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
            <Button size="small" onClick={backToSites}>← Sites</Button>
            <Breadcrumbs sx={{ flex: 1 }}>
              {trail.map((t, i) => (
                <Link key={i} component="button" onClick={() => gotoCrumb(i)} sx={{ fontWeight: i === trail.length - 1 ? 700 : 400 }}>
                  {t.name}
                </Link>
              ))}
            </Breadcrumbs>
            {(trail[trail.length - 1]?.web_url || (trail.length === 1 && site?.site_url)) && (
              <Tooltip title="Open deze map in SharePoint">
                <Button size="small" variant="text" component="a" target="_blank" rel="noopener noreferrer"
                  href={trail[trail.length - 1]?.web_url || site?.site_url}
                  sx={{ textTransform: 'none', color: '#64748b', fontWeight: 700 }}>
                  ↗ Open in SharePoint
                </Button>
              </Tooltip>
            )}
            <Tooltip title={librariesLevel ? 'Kies eerst een bibliotheek — op dit niveau zou een scan alleen de standaardbibliotheek pakken.' : ''}>
              <span>
                <Button size="small" variant="outlined" onClick={runScan} disabled={scan?.loading || librariesLevel}>
                  {scan?.loading ? <CircularProgress size={14} /> : '🔎 Grootste bestanden (≥50 MB)'}
                </Button>
              </span>
            </Tooltip>
            <Tooltip title={librariesLevel ? 'Kies eerst een bibliotheek — op dit niveau zou een scan alleen de standaardbibliotheek pakken.' : 'Loopt de héle map/bibliotheek af: media-mappen (ruw beeld/foto), PowerPoints >100 MB en totalen per soort. Kan even duren.'}>
              <span>
                <Button size="small" variant="contained" color="warning" onClick={runDeepScan} disabled={deep?.running || librariesLevel}>
                  {deep?.running ? <><CircularProgress size={14} sx={{ mr: 1 }} />Analyseren…</> : (deep && deep.totals_gb ? '🔄 Opnieuw analyseren' : '🧭 Diepe analyse')}
                </Button>
              </span>
            </Tooltip>
          </Stack>
          {librariesLevel && (
            <Alert severity="info" sx={{ mb: 1.5 }}>Deze site heeft meerdere bibliotheken — kies er hieronder één om te scannen.</Alert>
          )}

          {/* Lopende achtergrond-scan: voortgang tonen (mag minuten duren voor grote mappen). */}
          {deep?.running && (
            <Paper variant="outlined" sx={{ mb: 2, p: 2, display: 'flex', alignItems: 'center', gap: 1.5, bgcolor: '#fff7ed' }}>
              <CircularProgress size={20} />
              <Box>
                <Typography variant="body2" fontWeight={700}>Diepe analyse loopt…</Typography>
                <Typography variant="caption" color="text.secondary">
                  {(deep.scanned_folders || 0).toLocaleString('nl-NL')} mappen gescand — je kunt gerust wegklikken; het resultaat wordt bewaard.
                </Typography>
              </Box>
            </Paper>
          )}

          {/* Diepe analyse — media-mappen, grote PowerPoints, totalen per soort */}
          {deep && !deep.running && deep.totals_gb && (
            <Paper variant="outlined" sx={{ mb: 2, overflow: 'hidden' }}>
              <Box sx={{ px: 2, py: 1, bgcolor: '#fff7ed', display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="body2" fontWeight={700}>🧭 Diepe analyse</Typography>
                {deep.saved_at && (
                  <Chip size="small" variant="outlined" color={deep.fromCache ? 'default' : 'success'}
                    label={`${deep.fromCache ? 'Opgeslagen' : 'Zojuist'} ${new Date(deep.saved_at).toLocaleString('nl-NL', { dateStyle: 'short', timeStyle: 'short' })}${deep.created_by ? ` · ${deep.created_by}` : ''}`} />
                )}
                <Chip size="small" label={`Huidig ${deep.totals_gb?.total ?? 0} GB`} />
                {deep.versions && (
                  <Tooltip title={`Gemeten bij ${deep.versions.checked_files} van ${deep.versions.candidate_files} bestanden (Office ≥ 1 MB en grote bestanden). Dit is een ondergrens.`}>
                    <Chip size="small" label={`🕘 Oude versies ${deep.versions.versions_gb} GB · totaal ${deep.versions.total_gb} GB`} sx={{ bgcolor: '#ffedd5', color: VERSIE_KLEUR, fontWeight: 700 }} />
                  </Tooltip>
                )}
                <Chip size="small" label={`🎬 Footage ${deep.totals_gb?.footage ?? 0} GB`} sx={{ bgcolor: '#fee2e2', color: '#991b1b' }} />
                <Chip size="small" label={`📷 Foto ${deep.totals_gb?.photo ?? 0} GB`} sx={{ bgcolor: '#ffedd5', color: '#9a3412' }} />
                <Chip size="small" label={`📊 PowerPoint ${deep.totals_gb?.powerpoint ?? 0} GB`} sx={{ bgcolor: '#fef9c3', color: '#854d0e' }} />
                <Chip size="small" label={`${(deep.file_count || 0).toLocaleString('nl-NL')} bestanden`} variant="outlined" />
                <Button size="small" onClick={() => setDeep(null)} sx={{ ml: 'auto' }}>Sluiten</Button>
              </Box>
              {deep.truncated && (
                <Alert severity="info" sx={{ borderRadius: 0 }}>
                  Heel groot — het scan-plafond ({deep.scanned_folders} mappen) is geraakt. De totalen dekken het gescande deel;
                  open een specifieke top-map en draai 'Diepe analyse' opnieuw voor de rest.
                </Alert>
              )}

              {/* Media-mappen (ruw beeld / foto) → NAS-kandidaten */}
              <Box sx={{ px: 2, pt: 1.5 }}>
                <Typography variant="caption" fontWeight={700} color="text.secondary">📦 Mappen met het meeste ruw materiaal (→ horen op de NAS)</Typography>
              </Box>
              <Table size="small">
                <TableBody>
                  {(deep.media_folders || []).map((f) => (
                    <TableRow key={f.id || f.name} hover>
                      <TableCell sx={{ width: 32 }}>{f.kind === 'footage' ? '🎬' : '📷'}</TableCell>
                      <TableCell>
                        {f.web_url
                          ? <Link href={f.web_url} target="_blank" rel="noopener noreferrer" sx={{ fontWeight: 600 }}>{f.name}</Link>
                          : <Typography variant="body2" fontWeight={600}>{f.name}</Typography>}
                        <Chip size="small" label={f.kind === 'footage' ? 'ruw beeld' : 'foto'} sx={{ ml: 1, height: 18, fontSize: 11, bgcolor: '#fff7ed', color: '#c2410c' }} />
                        {f.is_media_name && <Chip size="small" label="→ NAS" sx={{ ml: 0.5, height: 18, fontSize: 11, bgcolor: '#fee2e2', color: '#991b1b' }} />}
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}><b>{f.media_gb} GB</b> media</TableCell>
                    </TableRow>
                  ))}
                  {(deep.media_folders || []).length === 0 && (
                    <TableRow><TableCell colSpan={3}><Typography variant="caption" color="text.disabled">Geen mappen met veel ruw beeld/foto gevonden.</Typography></TableCell></TableRow>
                  )}
                </TableBody>
              </Table>

              {/* Bestanden met de meeste oude versies: hier valt met opschonen het meest te winnen */}
              {deep.versions && (
                <>
                  <Box sx={{ px: 2, pt: 1.5 }}>
                    <Typography variant="caption" fontWeight={700} sx={{ color: VERSIE_KLEUR }}>
                      🕘 Bestanden met de meeste oude versies {deep.versions.unchecked_files ? `· ${deep.versions.unchecked_files} bestanden niet gemeten (tijd of throttling)` : ''}
                    </Typography>
                  </Box>
                  <Table size="small">
                    <TableBody>
                      {(deep.version_files || []).map((f) => (
                        <TableRow key={f.id} hover>
                          <TableCell sx={{ width: 32 }}>🕘</TableCell>
                          <TableCell>
                            <Link href={f.web_url} target="_blank" rel="noopener noreferrer">{f.name}</Link>
                            <Typography variant="caption" sx={{ display: 'block', color: '#94a3b8' }}>{f.path}</Typography>
                          </TableCell>
                          <GrootteCel f={f} />
                          <TableCell align="right"><IconButton size="small" onClick={() => openVersions(f)}><HistoryIcon fontSize="small" /></IconButton></TableCell>
                        </TableRow>
                      ))}
                      {(deep.version_files || []).length === 0 && (
                        <TableRow><TableCell colSpan={4}><Typography variant="caption" color="text.disabled">Geen oude versies gevonden bij de gemeten bestanden.</Typography></TableCell></TableRow>
                      )}
                    </TableBody>
                  </Table>
                </>
              )}

              {/* PowerPoints > 100 MB */}
              <Box sx={{ px: 2, pt: 1.5 }}>
                <Typography variant="caption" fontWeight={700} color="text.secondary">📊 PowerPoints groter dan 100 MB {(deep.large_powerpoints || []).length ? `(${deep.large_powerpoints.length})` : ''}</Typography>
              </Box>
              <Table size="small">
                <TableBody>
                  {(deep.large_powerpoints || []).map((f) => (
                    <TableRow key={f.id} hover>
                      <TableCell sx={{ width: 32 }}>📊</TableCell>
                      <TableCell>
                        <Link href={f.web_url} target="_blank" rel="noopener noreferrer">{f.name}</Link>
                        <Typography variant="caption" sx={{ display: 'block', color: '#94a3b8' }}>
                          {folderUrl(f.web_url)
                            ? <Link href={folderUrl(f.web_url)} target="_blank" rel="noopener noreferrer" title="Open de map in SharePoint (om te verwijderen)" sx={{ color: '#64748b' }}>📂 {f.path}</Link>
                            : f.path}
                        </Typography>
                      </TableCell>
                      <GrootteCel f={f} />
                      <TableCell align="right"><IconButton size="small" onClick={() => openVersions(f)}><HistoryIcon fontSize="small" /></IconButton></TableCell>
                    </TableRow>
                  ))}
                  {(deep.large_powerpoints || []).length === 0 && (
                    <TableRow><TableCell colSpan={4}><Typography variant="caption" color="text.disabled">Geen PowerPoints boven 100 MB.</Typography></TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </Paper>
          )}

          {/* Recursieve scan → direct op bestandsniveau */}
          {scan && !scan.loading && (
            <Paper variant="outlined" sx={{ mb: 2, overflow: 'hidden' }}>
              <Box sx={{ px: 2, py: 1, bgcolor: '#fff7ed', display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" fontWeight={700}>
                  Grootste bestanden {scan.truncated ? '· gedeeltelijk gescand (verfijn per map voor de rest)' : ''}
                </Typography>
                <Button size="small" onClick={() => setScan(null)} sx={{ ml: 'auto' }}>Sluiten</Button>
              </Box>
              <Table size="small"><TableBody>
                {scan.files.map((f) => (
                  <TableRow key={f.id} hover>
                    <TableCell sx={{ width: 32 }}><InsertDriveFileOutlinedIcon fontSize="small" sx={{ color: f.is_media ? '#ea580c' : '#cbd5e1' }} /></TableCell>
                    <TableCell>
                      <Link href={f.web_url} target="_blank" rel="noopener noreferrer" sx={{ textDecoration: 'none' }}>{f.name}</Link>
                      {f.is_media && <Chip size="small" label="media" sx={{ ml: 1, height: 18, fontSize: 11, bgcolor: '#fff7ed', color: '#c2410c' }} />}
                      <Typography variant="caption" sx={{ display: 'block', color: '#94a3b8' }}>
                        {folderUrl(f.web_url)
                          ? <Link href={folderUrl(f.web_url)} target="_blank" rel="noopener noreferrer" title="Open de map in SharePoint (om te verwijderen)" sx={{ color: '#64748b' }}>📂 {f.path}</Link>
                          : f.path}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}><b>{f.size_gb} GB</b></TableCell>
                    <TableCell align="right">
                      {f.is_office && <IconButton size="small" onClick={() => openVersions(f)}><HistoryIcon fontSize="small" /></IconButton>}
                    </TableCell>
                  </TableRow>
                ))}
                {scan.files.length === 0 && (
                  <TableRow><TableCell colSpan={4}><Typography variant="caption" color="text.disabled">Geen bestanden ≥ 50 MB in dit deel.</Typography></TableCell></TableRow>
                )}
              </TableBody></Table>
            </Paper>
          )}
          {!browsing && content?.site_totals && (
            <Opslagsplitsing titel={content.site_totals.subsites ? `Hele site (incl. ${content.site_totals.subsites} subsite${content.site_totals.subsites > 1 ? 's' : ''})` : 'Hele site'} huidig={content.site_totals.current_gb} versies={content.site_totals.versions_gb}
              totaal={content.site_totals.total_gb} versieLabel="oude versies + prullenbak"
              toelichting="Totaal = wat SharePoint rekent voor de opslaglimiet; het verschil met de huidige bestanden zijn vooral oude versies, plus prullenbak en bewaarbibliotheek." />
          )}
          {!browsing && content?.totals && (content.totals.measured > 0 || !content.site_totals) && (
            <Opslagsplitsing titel="In deze lijst" huidig={content.totals.current_gb} versies={content.totals.versions_gb} totaal={content.totals.total_gb}
              toelichting={content.totals.unmeasured
                ? `${content.totals.measured} van ${content.totals.measured + content.totals.unmeasured} onderdelen gemeten; ${content.totals.unmeasured_gb} GB is zonder versies meegeteld${content.metrics_source === 'graph' ? ' (voor mappen geen versiecijfer: SharePoint-certificaat ontbreekt of de map gaf geen opslagvelden)' : ''}. "Diepe analyse" meet de versies per bestand in de hele map.`
                : 'Alles in deze lijst is inclusief oude versies gemeten.'} />
          )}
          {browsing ? <CircularProgress size={22} /> : content && (
            <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
              <Table size="small">
                <TableBody>
                  {content.folders.map((f) => (
                    <TableRow key={f.id || f.drive_id || f.name} hover>
                      <TableCell sx={{ width: 32 }}><FolderIcon fontSize="small" sx={{ color: f.is_library ? '#7c3aed' : (f.is_media ? '#ea580c' : '#94a3b8') }} /></TableCell>
                      <TableCell>
                        <Link component="button" onClick={() => enterFolder(f)} sx={{ fontWeight: 600, textAlign: 'left' }}>{f.name}</Link>
                        {f.is_library && <Chip size="small" label="bibliotheek" sx={{ ml: 1, height: 18, fontSize: 11, bgcolor: '#ede9fe', color: '#6d28d9' }} />}
                        {f.is_media && <Chip size="small" label="→ hoort op de NAS" sx={{ ml: 1, height: 18, fontSize: 11, bgcolor: '#fff7ed', color: '#c2410c' }} />}
                        {f.web_url && (
                          <Tooltip title="Open deze map in SharePoint">
                            <Link href={f.web_url} target="_blank" rel="noopener noreferrer"
                              sx={{ ml: 1, color: '#64748b', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}>↗ SharePoint</Link>
                          </Tooltip>
                        )}
                      </TableCell>
                      <GrootteCel f={f} toonOnbekend={content.totals?.measured > 0} />
                      <TableCell align="right" sx={{ color: '#94a3b8', fontSize: 12 }}>{f.child_count} items</TableCell>
                    </TableRow>
                  ))}
                  {content.files.map((f) => (
                    <TableRow key={f.id} hover>
                      <TableCell><InsertDriveFileOutlinedIcon fontSize="small" sx={{ color: f.is_media ? '#ea580c' : '#cbd5e1' }} /></TableCell>
                      <TableCell>
                        <Link href={f.web_url} target="_blank" rel="noopener noreferrer" sx={{ textDecoration: 'none' }}>{f.name}</Link>
                        {f.is_media && <Chip size="small" label="media" sx={{ ml: 1, height: 18, fontSize: 11, bgcolor: '#fff7ed', color: '#c2410c' }} />}
                      </TableCell>
                      <GrootteCel f={f} vet={false} toonOnbekend={content.totals?.measured > 0} />
                      <TableCell align="right">
                        {f.is_office && (
                          <Tooltip title="Versiegeschiedenis / opblaas bekijken">
                            <IconButton size="small" onClick={() => openVersions(f)}><HistoryIcon fontSize="small" /></IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {content.folders.length === 0 && content.files.length === 0 && (
                    <TableRow><TableCell colSpan={4}><Typography variant="caption" color="text.disabled">Lege map.</Typography></TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </Paper>
          )}
        </>
      )}

      {/* Versies-dialoog */}
      <Dialog open={!!verDlg} onClose={() => setVerDlg(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>Versies — {verDlg?.file?.name}</DialogTitle>
        <DialogContent dividers>
          {verDlg?.loading ? <CircularProgress size={22} /> : verDlg?.data?.error ? (
            <Alert severity="error">{verDlg.data.error}</Alert>
          ) : verDlg?.data && (
            <>
              <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                <Chip label={`${verDlg.data.count} versies`} />
                <Chip label={`Huidig ${verDlg.data.current_gb} GB`} />
                <Chip label={`Opblaas ${verDlg.data.bloat_gb} GB`} color={verDlg.data.bloat_gb > 0.1 ? 'warning' : 'default'} sx={{ fontWeight: 700 }} />
              </Stack>
              <Table size="small">
                <TableHead><TableRow><TableCell>Gewijzigd</TableCell><TableCell>Door</TableCell><TableCell align="right">Grootte</TableCell></TableRow></TableHead>
                <TableBody>
                  {(verDlg.data.versions || []).map((v) => (
                    <TableRow key={v.id}><TableCell>{v.modified}</TableCell><TableCell>{v.by}</TableCell><TableCell align="right">{v.size_gb} GB</TableCell></TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVerDlg(null)}>Sluiten</Button>
          {verDlg?.data && !verDlg?.data?.error && verDlg.data.bloat_gb > 0 && (
            canEdit ? (
              <Button variant="contained" color="warning" disabled={verDlg.loading} onClick={() => trimVersions(verDlg.file)}>
                Oude versies opschonen ({verDlg.data.bloat_gb} GB)
              </Button>
            ) : (
              <Tooltip title="Je hebt alleen leesrechten voor SharePoint — opschonen (versies verwijderen) vereist bewerkrechten.">
                <span><Button variant="contained" color="warning" disabled>Opschonen ({verDlg.data.bloat_gb} GB) — alleen-lezen</Button></span>
              </Tooltip>
            )
          )}
        </DialogActions>
      </Dialog>

      {toast && <Alert severity="success" sx={{ mt: 2 }} onClose={() => setToast(null)}>{toast}</Alert>}
    </Box>
  );
}
